/**
 * The auth lifecycle (D8), in one component.
 *
 * What it fixes: putting the Google ID token in `localStorage` and checking
 * `exp` once, on mount. A tab open longer than an hour then sends a dead token on every
 * request and the player sees opaque failures with no way back. Here the token is held in
 * memory, mirrored to `sessionStorage` for reloads (session.ts), renewed silently *before*
 * `exp`, and any 401 from anywhere in the app — including one that arrives mid-turn —
 * drops the session cleanly while the router keeps the route (RequireAuth.tsx).
 *
 * A 401 during an open turn stream does not lose the turn. The server finishes and commits
 * regardless (`src/quest-v2-api/README.md` §3: "a dropped connection must not cost a turn"), so
 * the recovery is re-auth and refetch, never re-submit.
 */
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { onUnauthenticated, setAuthTokenProvider } from '@/api/client';
import {
  AuthContext,
  type AuthStatus,
  type IdentityStatus,
  type SignedOutReason,
} from '@/auth/context';
import { createDevCredential } from '@/auth/devToken';
import { loadGoogleIdentity, type GoogleIdentity } from '@/auth/gis';
import { clearSessionToken, readSessionToken, writeSessionToken } from '@/auth/session';
import { decodeIdToken, timeToExpiry, type IdToken } from '@/auth/token';

/** Renew this long before `exp`. Google's ID tokens live an hour; five minutes is slack. */
const RENEW_MARGIN_MS = 5 * 60 * 1000;

/** `setTimeout` overflows past 2^31-1 ms and fires *immediately* — the opposite of never. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const MISSING_CLIENT_ID =
  'VITE_GOOGLE_CLIENT_ID is not set. Copy.env.example to.env.local and fill it in.';

interface AuthState {
  status: AuthStatus;
  token: IdToken | null;
  reason: SignedOutReason;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
  const devSignInAvailable = import.meta.env.DEV && clientId === '';

  // Read synchronously, not in an effect: this is what makes a reload into /play/{id} land
  // signed in on the first frame instead of bouncing through /login and back.
  const [state, setState] = useState<AuthState>(() => {
    const stored = readSessionToken();
    return stored
      ? { status: 'signed_in', token: stored, reason: null }
      : { status: 'signed_out', token: null, reason: null };
  });

  const [identityStatus, setIdentityStatus] = useState<IdentityStatus>(() =>
    clientId === '' ? 'unavailable' : 'loading',
  );
  const [identityError, setIdentityError] = useState<string | null>(() =>
    clientId === '' ? MISSING_CLIENT_ID : null,
  );

  const identityRef = useRef<GoogleIdentity | null>(null);

  /**
   * The client knows how to send a bearer token; it does not know where tokens come from.
   * This is the only wire between the two, and it is one function (client.ts).
   *
   * **Installed during render, not in an effect** — and that is a bug fix, found by the first-run test of the peripheral surfaces'
   * first-run test (`tests/mocks/firstRun.test.tsx`) the first time anything rendered the real
   * `App` against a server that actually checks the header. Effects run **children first**, so
   * the first route's `useQuery` mounted and fetched before any effect of this provider could
   * run. Any load that *starts* signed in — every reload, every return to a bookmarked
   * `/play/{id}` — therefore sent its opening `GET /me` and `GET /stories` with no
   * `Authorization` at all, took a 401, and dropped a live session to *"your session expired"*
   * before it had drawn a frame. (Signing in from `/login` was unaffected: by the time the
   * state change renders a route, the effect has long since run, which is most of why this
   * survived the whole game UI.) This used to be an effect holding a ref, with a comment reasoning about
   * effect order *within* this component — the half of the ordering that was never the problem.
   *
   * Re-installed on every render rather than once, which is what makes the ref unnecessary:
   * the provider is a function of the current token, so rendering with a new token installs a
   * provider that returns it. The effect below stays for its cleanup alone — a remounting
   * provider must not leave a dead closure wired into a module-level client.
   */
  setAuthTokenProvider(() => state.token?.credential ?? null);

  useEffect(
    () => () => {
      setAuthTokenProvider(() => null);
    },
    [],
  );

  // Every 401, from every call site, lands here (D8: "401 as a first-class state").
  useEffect(
    () =>
      onUnauthenticated(() => {
        clearSessionToken();
        setState((current) =>
          current.status === 'signed_out'
            ? current
            : { status: 'signed_out', token: null, reason: 'expired' },
        );
      }),
    [],
  );

  useEffect(() => {
    if (clientId === '') return;
    let cancelled = false;

    loadGoogleIdentity()
      .then((identity) => {
        if (cancelled) return;
        identity.initialize({
          client_id: clientId,
          callback: (response) => {
            applyCredential(response.credential, setState);
          },
          // auto_select is what makes the silent renewal below silent: with a live Google
          // session, re-prompting returns a fresh credential without a click.
          auto_select: true,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true,
        });
        identityRef.current = identity;
        setIdentityStatus('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Offline, blocked by an extension, or refused by CSP. Never a blank page: the
        // login route renders this string.
        setIdentityStatus('unavailable');
        setIdentityError(error instanceof Error ? error.message : 'Sign-in is unavailable.');
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Renew before `exp`, and give up exactly at it. The second timer is the one that matters:
  // an expired token in hand means every subsequent request 401s, so the honest state is
  // signed out — with the route preserved, which is the whole point.
  useEffect(() => {
    const token = state.token;
    if (token === null) return;

    const lifetime = timeToExpiry(token);
    const renew = setTimeout(
      () => identityRef.current?.prompt(),
      Math.min(Math.max(0, lifetime - RENEW_MARGIN_MS), MAX_TIMEOUT_MS),
    );
    const expire = setTimeout(
      () => {
        clearSessionToken();
        setState({ status: 'signed_out', token: null, reason: 'expired' });
      },
      Math.min(lifetime, MAX_TIMEOUT_MS),
    );

    return () => {
      clearTimeout(renew);
      clearTimeout(expire);
    };
  }, [state.token]);

  const value = {
    status: state.status,
    token: state.token,
    reason: state.reason,
    identityStatus,
    identityError,

    signIn: () => {
      if (devSignInAvailable) {
        applyCredential(createDevCredential(), setState);
        return;
      }
      identityRef.current?.prompt();
    },

    signOut: () => {
      // Without disableAutoSelect, One Tap signs the player straight back in and "sign out"
      // reads as a bug.
      identityRef.current?.disableAutoSelect();
      clearSessionToken();
      setState({ status: 'signed_out', token: null, reason: 'signed_out' });
    },

    renderButton: (element: HTMLElement | null) => {
      if (element === null || identityRef.current === null) return;
      element.replaceChildren();
      identityRef.current.renderButton(element, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
      });
    },
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

function applyCredential(credential: string, setState: Dispatch<SetStateAction<AuthState>>): void {
  const token = decodeIdToken(credential);
  if (token === null) {
    // Google handed back something unreadable. Treat it as no session rather than storing
    // a credential we cannot reason about the expiry of.
    clearSessionToken();
    setState({ status: 'signed_out', token: null, reason: 'expired' });
    return;
  }
  writeSessionToken(credential);
  setState({ status: 'signed_in', token, reason: null });
}
